import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  FileDownload as FileIcon,
  Archive as ZipIcon,
  CheckCircle as CheckIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import JSZip from 'jszip';
import InteractivePlanPreview from './InteractivePlanPreview';
import { generateInteractivePlanHtml } from '../services/interactivePlanGenerator';
import { downloadDocument, downloadDocumentAuthenticated } from '../services/documentService';
import { getActiveSessionId } from '../utils/sessionManagerV2';
import { fetchAuthSession } from 'aws-amplify/auth';
import getEnvironmentConfig from '../config/environment';

const DocumentDownload = ({
  documents = [],
  sessionId: propSessionId,
  renderVars = null,
  onBack,
  onStartNewClient
}) => {
  const [downloading, setDownloading] = useState({});
  const [downloadComplete, setDownloadComplete] = useState({});
  const [creatingZip, setCreatingZip] = useState(false);
  const [sessionId, setSessionId] = useState(propSessionId || null);

  // Get API endpoint for downloads
  const config = getEnvironmentConfig();
  const DOC_CREATION_API_BASE = config.aws_cloud_logic_custom.find(api => api.name === 'DocCreationAPI')?.endpoint;

  // Get session ID on mount - prioritize prop over fetching new session
  useEffect(() => {
    const fetchSessionId = async () => {
      // Always prioritize sessionId from props
      const sessionToUse = propSessionId || await getActiveSessionId();

      if (propSessionId) {
        console.log('DocumentDownload: Using sessionId from props:', propSessionId);
      } else {
        console.log('DocumentDownload: Fetched sessionId from server:', sessionToUse);
      }

      setSessionId(sessionToUse);
    };
    fetchSessionId();
  }, [propSessionId]);

  const handleDownloadSingle = async (doc) => {
    try {
      setDownloading(prev => ({ ...prev, [doc.id]: true }));

      // Extract document key from URL
      const docKey = doc.url.split('/').pop();

      console.log('Downloading single document:', { docKey, sessionId });

      // Use authenticated download with sessionId
      const result = await downloadDocumentAuthenticated(docKey, false, sessionId);

      if (!result.success) {
        throw new Error(result.error || 'Download failed');
      }

      // Mark as complete
      setDownloadComplete(prev => ({ ...prev, [doc.id]: true }));

      // Reset after delay
      setTimeout(() => {
        setDownloadComplete(prev => ({ ...prev, [doc.id]: false }));
      }, 3000);

    } catch (error) {
      console.error('Error downloading document:', error);
      alert(`Failed to download ${doc.name}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const handleDownloadAll = async () => {
    try {
      setCreatingZip(true);

      // Get authentication token
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('Authentication required');
      }

      // Create new ZIP instance
      const zip = new JSZip();

      // Add the Interactive Plan HTML first (brief-import flow only).
      // Hoisted ahead of the docx fetches so a network hiccup downloading the
      // docx files can never prevent the HTML from making it into the ZIP.
      let interactiveAdded = false;
      if (renderVars && renderVars.client) {
        try {
          const html = generateInteractivePlanHtml(renderVars);
          zip.file('Interactive_Plan.html', html);
          interactiveAdded = true;
          console.log('Added Interactive_Plan.html to ZIP', { bytes: html.length });
        } catch (err) {
          console.error('Failed to add Interactive Plan to ZIP:', err);
        }
      }

      // Add each document to the ZIP
      const failedDocs = [];
      for (const doc of documents) {
        try {
          // Extract document key from URL
          const docKey = doc.url.split('/').pop();

          // Build authenticated download URL with sessionId
          const downloadUrl = `${DOC_CREATION_API_BASE}/download/${docKey}?sessionId=${encodeURIComponent(sessionId)}`;

          console.log('Fetching document for ZIP:', { docKey, sessionId });

          // Fetch the document with authentication
          const response = await fetch(downloadUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${idToken}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch ${doc.name}: ${response.status} ${response.statusText}`);
          }

          const blob = await response.blob();
          const filename = `${doc.name.replace(/\s+/g, '_')}.docx`;

          // Add to ZIP
          zip.file(filename, blob);

        } catch (error) {
          console.error(`Error adding ${doc.name} to ZIP:`, error);
          failedDocs.push(doc.name);
          // Continue with other documents
        }
      }

      // Generate ZIP file
      const content = await zip.generateAsync({ type: "blob" });

      // Create download link
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Estate_Documents_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Surface what made it into the ZIP so the user can tell at a glance
      const includedCount = documents.length - failedDocs.length + (interactiveAdded ? 1 : 0);
      const lines = [`Downloaded ${includedCount} file(s).`];
      if (interactiveAdded) lines.push('Includes Interactive_Plan.html.');
      if (failedDocs.length) lines.push(`Failed: ${failedDocs.join(', ')}.`);
      alert(lines.join('\n'));

    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Failed to create ZIP file. Please download documents individually.');
    } finally {
      setCreatingZip(false);
    }
  };

  const handlePreview = async (doc) => {
    // Note: Preview requires authentication, so we download and open instead
    try {
      // Extract document key from URL
      const docKey = doc.url.split('/').pop();

      console.log('Previewing document:', { docKey, sessionId });

      // Get authentication token
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        alert('Authentication required for preview');
        return;
      }

      // Build authenticated download URL with sessionId
      const downloadUrl = `${DOC_CREATION_API_BASE}/download/${docKey}?sessionId=${encodeURIComponent(sessionId)}`;

      // Fetch the document with authentication
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Preview failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

    } catch (error) {
      console.error('Error previewing document:', error);
      alert(`Failed to preview ${doc.name}. Please try downloading instead.`);
    }
  };

  const getDocumentSize = (doc) => {
    // Estimate based on document type
    const sizes = {
      'Will': '~125 KB',
      'Trust': '~350 KB',
      'Financial Power of Attorney': '~85 KB',
      'Healthcare Power of Attorney': '~75 KB',
      'HIPAA Authorization': '~45 KB',
      'Advance Directive': '~65 KB',
      'Operating Agreement': '~180 KB'
    };
    return sizes[doc.name] || '~100 KB';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Download Your Documents
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Your documents are ready! Download them individually or all together as a ZIP file.
      </Typography>

      {/* Success Alert */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="subtitle2">
          ✅ All documents have been generated and customized successfully
        </Typography>
      </Alert>

      {/* Interactive Plan — only when we have render variables */}
      {renderVars && (
        <InteractivePlanPreview renderVars={renderVars} />
      )}

      {/* Document List */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Documents ({documents.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={creatingZip ? <CircularProgress size={16} color="inherit" /> : <ZipIcon />}
            onClick={handleDownloadAll}
            disabled={creatingZip || documents.length === 0}
          >
            {creatingZip ? 'Creating ZIP...' : 'Download All as ZIP'}
          </Button>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <List>
          {documents.map((doc, index) => (
            <Box key={doc.id}>
              {index > 0 && <Divider />}
              <ListItem>
                <ListItemIcon>
                  <FileIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {doc.name}
                      {doc.isEdited && (
                        <Chip label="Edited" size="small" color="success" />
                      )}
                      <Chip label={getDocumentSize(doc)} size="small" variant="outlined" />
                      {downloadComplete[doc.id] && (
                        <CheckIcon color="success" fontSize="small" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" component="span">
                        Modified: {new Date().toLocaleString()}
                      </Typography>
                      {doc.key && (
                        <Typography variant="caption" component="span" sx={{ ml: 2, fontSize: '0.7rem', opacity: 0.7 }}>
                          ({doc.key})
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Preview">
                      <IconButton onClick={() => handlePreview(doc)} size="small">
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        downloading[doc.id] ? 
                        <CircularProgress size={16} /> : 
                        <DownloadIcon />
                      }
                      onClick={() => handleDownloadSingle(doc)}
                      disabled={downloading[doc.id]}
                    >
                      {downloading[doc.id] ? 'Downloading...' : 'Download'}
                    </Button>
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
            </Box>
          ))}
        </List>
      </Paper>

      {/* Next Steps */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.light' }}>
        <Typography variant="h6" gutterBottom>
          Next Steps
        </Typography>
        <Typography variant="body2" paragraph>
          1. Review all documents carefully with your client
        </Typography>
        <Typography variant="body2" paragraph>
          2. Have documents properly executed (signed, witnessed, and notarized as required)
        </Typography>
        <Typography variant="body2" paragraph>
          3. Distribute copies to relevant parties (trustees, agents, healthcare providers)
        </Typography>
        <Typography variant="body2">
          4. Store original documents in a secure location
        </Typography>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          onClick={onBack}
          size="large"
        >
          Back to Editing
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onStartNewClient}
          startIcon={<CheckIcon />}
        >
          Start New Client
        </Button>
      </Box>
    </Box>
  );
};

export default DocumentDownload;